import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Eyebrow } from "../components/Primitives";

const LINES = [
    "Analyzing your responses across 24 dimensions…",
    "Mapping your attachment patterns…",
    "Identifying your compatibility blind spots…",
    "Matching you to your love archetype…",
];

export default function Analysis({ onDone }) {
    const [lineIdx, setLineIdx] = useState(0);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const lineInt = setInterval(() => {
            setLineIdx((i) => (i + 1) % LINES.length);
        }, 1500);
        const start = Date.now();
        const progInt = setInterval(() => {
            const pct = Math.min(100, ((Date.now() - start) / 7000) * 100);
            setProgress(pct);
        }, 60);
        const done = setTimeout(() => onDone(), 7000);
        return () => {
            clearInterval(lineInt);
            clearInterval(progInt);
            clearTimeout(done);
        };
    }, [onDone]);

    return (
        <div className="flex-1 flex flex-col bg-cream relative grain" data-testid="analysis-screen">
            <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
                <Eyebrow className="mb-12">Calibrating</Eyebrow>

                {/* Pulsing core */}
                <div className="relative w-44 h-44 flex items-center justify-center">
                    <motion.div
                        className="absolute inset-0 rounded-full border border-burgundy/20"
                        animate={{ scale: [1, 1.15, 1], opacity: [0.6, 0.2, 0.6] }}
                        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                    />
                    <motion.div
                        className="absolute inset-3 rounded-full border border-gold/40"
                        animate={{ scale: [1, 1.1, 1], opacity: [0.4, 1, 0.4] }}
                        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                    />
                    <motion.div
                        className="w-20 h-20 rounded-full bg-gradient-to-br from-rose to-gold"
                        animate={{ scale: [1, 1.06, 1] }}
                        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                    />
                </div>

                <div className="mt-14 h-12 flex items-center">
                    <AnimatePresence mode="wait">
                        <motion.p
                            key={lineIdx}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.5 }}
                            className="font-display italic text-xl text-burgundy max-w-xs"
                            data-testid="analysis-line"
                        >
                            {LINES[lineIdx]}
                        </motion.p>
                    </AnimatePresence>
                </div>

                <p className="mt-6 text-[10px] tracking-[0.32em] uppercase text-ink-muted">
                    Built on our compatibility science
                </p>
            </div>

            {/* Progress at bottom */}
            <div className="px-8 pb-12">
                <div className="w-full h-[2px] bg-burgundy/10">
                    <motion.div
                        className="h-full bg-burgundy"
                        animate={{ width: `${progress}%` }}
                        transition={{ ease: "linear", duration: 0.06 }}
                        data-testid="analysis-progress"
                    />
                </div>
                <div className="mt-3 flex justify-between text-[10px] tracking-[0.28em] uppercase text-ink-muted">
                    <span>0%</span>
                    <span>{Math.floor(progress)}%</span>
                </div>
            </div>
        </div>
    );
}
